"use client"

import { useParams, usePathname } from "next/navigation"
import { useMemo } from "react"

// Route names that appear as the first segment when there is no [region] in the path
// (e.g. /reports, /base). When the first segment is one of these, it is NOT a region slug.
const REGION_AWARE_ROOTS = new Set([
  "home", "reports", "homeowners", "work-orders", "volunteers", "base",
  "migration", "partners", "admin",
])
// Top-level (dashboard) routes that do not live under [region]
const TOP_LEVEL_NON_REGION = new Set(["profile", "settings", "tools-supplies", "messages"])

/**
 * Hook to get the current region from the URL
 *
 * In the App Router, useParams() in a parent layout does NOT include params from
 * child dynamic segments (e.g. [region]). So when the sidebar is rendered by the
 * (dashboard) layout, params.region is undefined. We fall back to parsing the
 * pathname: if the first segment is not a known route name, we treat it as the
 * region slug (e.g. /swannanoa-valley/work-orders/123 -> "swannanoa-valley").
 *
 * @returns Object with:
 *   - regionSlug: The region slug from the URL (e.g., "north-carolina")
 *
 * @example
 * ```tsx
 * function MyComponent() {
 *   const { regionSlug } = useRegion()
 *
 *   if (!regionSlug) {
 *     return <div>No region selected</div>
 *   }
 *
 *   return <div>Current region: {regionSlug}</div>
 * }
 * ```
 */
export function useRegion() {
  const params = useParams()
  const pathname = usePathname()

  const regionSlug = useMemo(() => {
    // 1. Prefer params.region (available when the component is under the [region] segment)
    const routerParams = params as any
    if (routerParams && typeof routerParams === "object") {
      const region = routerParams.region
      if (typeof region === "string") return region
      if (Array.isArray(region) && region.length > 0) return region[0]
    }

    // 2. Fallback: parent layout does not receive [region] in params. Derive from pathname.
    // URL shape: /{region}/work-orders/123 or /{region}/homeowners, etc.
    if (!pathname || typeof pathname !== "string") return null
    const segments = pathname.split("/").filter(Boolean)
    const first = segments[0]
    if (!first) return null
    if (REGION_AWARE_ROOTS.has(first) || TOP_LEVEL_NON_REGION.has(first)) return null
    return first
  }, [params, pathname])

  return { regionSlug }
}

